import React, { useContext, useEffect, useState } from 'react';
// import '@arcgis/core/assets/esri/themes/light/main.css';
import "react-datepicker/dist/react-datepicker.css";
import '../../assets/styles/App.css';
import { Navbar, Nav, Container, Dropdown, NavDropdown, Spinner } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';
import { FaBuilding, FaEnvelope, FaKey, FaSignOutAlt, FaUserCircle, FaUserShield } from 'react-icons/fa';
import { NavLink, useNavigate } from 'react-router-dom';
import api, { publicApi } from '../../api/client';

/**
 * Extracts a readable account dropdown error from FastAPI responses.
 * FastAPI can return detail as a string or as a validation error array.
 */
const getDropdownErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((item) => item.msg).filter(Boolean).join(' ') || 'Unable to load account details.';
  }

  return error.response?.data?.message || 'Unable to load account details.';
};

/**
 * formatRole - Converts backend role values into display text for the user dropdown.
 *
 * @param {string} role - The backend role value for the logged-in user.
 * @returns {string} - The formatted role label.
 */
const formatRole = (role) => {
  if (!role) {
    return '';
  }

  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
};

/**
 * NavbarComponent - Displays the navigation bar for the web platform.
 * This component shows links for map and graph views, the current logged-in user, and a logout button.
 *
 * @component
 * @example
 * return (
 *   <NavbarComponent />
 * )
 * 
 * @param {Object} props - The component props (currently no props are passed).
 * @param {string} token - The authentication token from AuthContext.
 * @param {string} username - The logged-in username from AuthContext.
 * @param {function} logout - Function to log out the user from AuthContext.
 */
const NavbarComponent = () => {
  // Accessing authentication token, username, and logout function from AuthContext
  const { token, username, logout } = useContext(AuthContext);
  const navigate = useNavigate(); // Hook to navigate to login after logout

  // State hooks to manage account profile details in the user dropdown
  const [profileUser, setProfileUser] = useState(null); // Store current user profile data
  const [profileOrganisation, setProfileOrganisation] = useState(null); // Store current user's organisation data
  const [profileError, setProfileError] = useState(''); // Store profile loading error messages
  const [logoutError, setLogoutError] = useState(''); // Store logout error messages
  const [isProfileLoading, setIsProfileLoading] = useState(Boolean(token)); // State to track profile loading
  const [isLoggingOut, setIsLoggingOut] = useState(false); // State to track logout submission

  /**
   * useEffect - Loads the current user's profile and organisation for the account dropdown.
   * Profile data is shown for admin and viewer accounts after the role is returned by the backend.
   */
  useEffect(() => {
    const loadAccountProfile = async () => {
      if (!token) {
        setProfileUser(null);
        setProfileOrganisation(null);
        setIsProfileLoading(false);
        return;
      }

      setIsProfileLoading(true);
      setProfileError('');
      setProfileUser(null);
      setProfileOrganisation(null);

      try {
        // Send both identity requests through the refresh-aware API client.
        const [userResponse, organisationResponse] = await Promise.all([
          api.get('/api/v1/identity/users/me'),
          api.get('/api/v1/identity/organisations/me'),
        ]);

        // Store profile data returned by the FastAPI identity endpoints.
        setProfileUser(userResponse.data);
        setProfileOrganisation(organisationResponse.data);
      } catch (err) {
        setProfileError(getDropdownErrorMessage(err));
      } finally {
        setIsProfileLoading(false);
      }
    };

    loadAccountProfile();
  }, [token]);

  /**
   * handleLogout - Logs out the user by calling the backend logout endpoint and then clearing local auth.
   * This will clear the authentication token and other user-related data.
   */
  const handleLogout = async () => {
    const accessToken = localStorage.getItem('access_token'); // Read the latest access token for the logout request
    const refreshToken = localStorage.getItem('refresh_token'); // Read the latest refresh token for the logout request

    setIsLoggingOut(true);
    setLogoutError('');

    try {
      if (accessToken && refreshToken) {
        // Send both tokens to FastAPI so the backend can revoke them without triggering refresh.
        await publicApi.post('/api/v1/auth/logout', {
          access_token: accessToken,
          refresh_token: refreshToken,
        });
      }
    } catch (err) {
      // Store a logout error briefly, but still clear local auth so the user is not stuck in the app.
      setLogoutError(getDropdownErrorMessage(err));
    } finally {
      logout(); // Call logout function to clear the token
      setIsLoggingOut(false);
      navigate('/login');
    }
  };

  // const ls_username = localStorage.getItem("username");
  const profileRole = profileUser?.role?.toLowerCase(); // Normalise role value returned by the backend
  const shouldShowProfile = ['admin', 'viewer'].includes(profileRole); // Only admin and viewer accounts show profile details
  const shouldShowDashboards = profileRole === 'admin'; // Only admin accounts show the Dashboards navbar dropdown
  const shouldShowAdminPanel = profileRole === 'admin'; // Only admin accounts show the Admin Panel dropdown link
  const shouldShowProfileSkeleton = isProfileLoading && !profileUser; // Keep the dropdown toggle from flashing the stored email on refresh
  const displayName = profileUser?.full_name || username || 'Account'; // Prefer backend full name once profile data has loaded

  // All navigation routes in one array
  const navLinks = [
    { label: "Map View", path: "/" },
    // { label: "Graph View", path: "/graphview" },
    { label: "Flow Dashboard", path: "/flowdashboard"},
    { label: "ESG Portfolio", path: "/esgportfoliodashboard" },
    { label: "Carbon Portfolio", path: "/carbonportfoliodashboard" },
    { label: "RiverSafe Dashboard", path: "/riversafeportfolio" },
    { label: "Cow Collar Dashboard", path: "/cowcollardashboard" },
    { label: "FarmTwin Daily Dashboard", path: "/farmtwindailydashboard" },
    { label: "ABP/Sainsbury's Dashboard", path: "/abpsainsburysdashboard" }
  ];
    
  return (
    // Main Navbar component from react-bootstrap
    <Navbar expand="lg" className="navbar">
        <Container fluid>
          {/* Navbar brand section */}
          <Navbar.Brand as={NavLink} to="/">
            <img
              alt="AGRISMART logo"
              src="/android-chrome-192x192.png"
              width="30"
              height="30"
              className="d-inline-block align-top"
            />
            {' '} Web Platform
          </Navbar.Brand>

          {/* Toggle button for smaller screens */}
          <Navbar.Toggle aria-controls="basic-navbar-nav"/>

          {/* Collapsible Navbar content */}
          <Navbar.Collapse id="main-navbar" className="justify-content-between">
            {/* Navigation links for the Map and Graph views */}
            <Nav className="me-auto">
              <Nav.Link as={NavLink} to="/" end>Map View</Nav.Link>
              {/*<Nav.Link as={NavLink} to="/graphview" end>Graph View</Nav.Link>*/}

              {/* Show dashboard navigation only for admin accounts */}
              {shouldShowDashboards && (
                <NavDropdown 
                  title="Dashboards" 
                  id="dashboards-dropdown" 
                  className="nav-dropdown"
                  menuVariant="light"
                >
                  {navLinks
                    .filter(link => link.label.includes("Dashboard") || link.label.includes("Portfolio"))
                    .map(link => {
                      // if (link.allowedUsers && !link.allowedUsers.includes(ls_username)) return null;
                      return (
                        <NavDropdown.Item as={NavLink} key={link.path} to={link.path}>
                          {link.label}
                        </NavDropdown.Item>
                      );
                    })}
                </NavDropdown>
              )}
            </Nav>
            {/* If the token exists (meaning the user is logged in), show the logout button */}
            {token && (
              // Right-aligned section for displaying username and logout button
              <Dropdown align="end" className="user-dropdown ms-3">
                <Dropdown.Toggle as="button" className="user-dropdown-toggle">
                  {/* Displaying the current username */}
                  <FaUserCircle size={20} className="me-2" />
                  {shouldShowProfileSkeleton ? (
                    <span className="user-dropdown-toggle-text placeholder-glow" aria-label="Loading profile">
                      <Spinner animation="border" size="sm" className="me-2" />
                    </span>
                  ) : (
                    <span className="user-dropdown-toggle-text">{displayName}</span>
                  )}
                </Dropdown.Toggle>

                  <Dropdown.Menu>
                    {/* Show profile loading feedback while the account endpoints are being fetched */}
                    {isProfileLoading && (
                      <div className="user-dropdown-loading">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading profile...
                      </div>
                    )}

                    {/* Show profile details for admin and viewer accounts */}
                    {!isProfileLoading && shouldShowProfile && (
                      <div className="user-dropdown-profile">
                        <div className="user-dropdown-profile-header">
                          <FaUserCircle size={34} className="user-dropdown-profile-icon" />
                          <div>
                            <div className="user-dropdown-profile-name">{profileUser.full_name}</div>
                            <div className="user-dropdown-profile-role">{formatRole(profileUser.role)}</div>
                          </div>
                        </div>

                        <div className="user-dropdown-profile-row">
                          <FaEnvelope className="user-dropdown-profile-row-icon" />
                          <span>{profileUser.email}</span>
                        </div>

                        <div className="user-dropdown-profile-row">
                          <FaBuilding className="user-dropdown-profile-row-icon" />
                          <span>{profileOrganisation?.name || 'Organisation unavailable'}</span>
                        </div>
                      </div>
                    )}

                    {/* Show a quiet fallback if profile data cannot be loaded */}
                    {!isProfileLoading && profileError && (
                      <div className="user-dropdown-error">{profileError}</div>
                    )}

                    {/* Show logout feedback if the logout endpoint fails before local auth is cleared */}
                    {logoutError && <div className="user-dropdown-error">{logoutError}</div>}

                    <Dropdown.Divider />

                    {/* Show the Admin Panel action only for admin accounts */}
                    {!isProfileLoading && shouldShowAdminPanel && (
                      <Dropdown.Item as={NavLink} to="/admin">
                        <FaUserShield className="me-2" />
                        Admin Panel
                      </Dropdown.Item>
                    )}

                    {/* Show the change password action for admin and viewer accounts */}
                    {!isProfileLoading && shouldShowProfile && (
                      <Dropdown.Item as={NavLink} to="/change-password">
                        <FaKey className="me-2" />
                        Change password
                      </Dropdown.Item>
                    )}

                    <Dropdown.Item onClick={handleLogout} disabled={isLoggingOut}>
                      <FaSignOutAlt className="me-2" />
                      {isLoggingOut ? 'Logging out' : 'Logout'}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
            )}
              
          </Navbar.Collapse>
        </Container>
    </Navbar>
  );
};
  
export default NavbarComponent;
